import { useLayoutEffect, useRef } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GLYPH } from '../glyphs';
import { FormField } from './FormField';
import { type BaseFieldProps, describedBy, useBoundField, useFieldIds } from './fieldBase';
import { useRegisterFields } from './fieldRegistry';
import { toLocalizedText } from './localizedText';
import { useLocalizedInput } from './useLocalizedInput';

export interface RichTextFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
  /** Максимум символов на язык — счётчик «N / max». */
  maxLength?: number;
  /**
   * Кнопка «Изображение» и список изображений текста (разметка «![подпись](https://…)» уроков).
   * `false` — у текста другой формат вставок (публикации CNT: файл, а не ссылка) — без кнопки.
   */
  images?: boolean;
}

/** Изображение в тексте: «![подпись](https://…)» — блок с подписью (lrn/service/rich_text.py). */
const IMAGE_LINE = /^!\[([^\]]*)\]\((https:\/\/[^\s)]*)\)$/;

interface TextImage {
  line: number;
  caption: string;
  url: string;
}

function imagesOf(text: string): TextImage[] {
  return text.split('\n').flatMap((raw, line) => {
    const match = IMAGE_LINE.exec(raw.trim());
    return match ? [{ line, caption: match[1] ?? '', url: match[2] ?? '' }] : [];
  });
}

function wordsOf(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/** Блок добавляется в конец текста отдельным абзацем (как в прототипе `edInsert`). */
function appendBlock(text: string, snippet: string): string {
  const base = text.replace(/\n*$/, '');
  return base ? `${base}\n\n${snippet}` : snippet;
}

/**
 * Поле-редактор текста урока и описания курса (ТЗ 8.7.1–8.7.2, прототип `isEditor`): панель
 * «Подзаголовок», «Цитата», «Список», «Ссылка» и «Изображение» добавляют разметку абзацем в конец
 * текста, счётчик слов и изображений; под полем — изображения текста с «Убрать». Разметка — та,
 * что сервер превращает в блоки приложения: «## », «> », «- », «[текст](https://…)»,
 * «![подпись](https://…)». Значение — LocalizedText (языки рынка, как у `LocalizedTextField`).
 */
export function RichTextField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide = true,
  disabled,
  placeholder,
  maxLength,
  images: withImages = true,
}: RichTextFieldProps<TValues>) {
  const { t } = useTranslation('form');
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const input = useLocalizedInput(toLocalizedText(field.value), field.onChange);
  const area = useRef<HTMLTextAreaElement | null>(null);
  /** Позиция курсора после вставки разметки — ставится, когда новый текст уже в поле. */
  const caret = useRef<number | null>(null);
  useRegisterFields(input.languages.map((code) => `${name}.${code}`));

  useLayoutEffect(() => {
    const node = area.current;
    const position = caret.current;
    if (!node || position === null || node.value.length < position) return;
    caret.current = null;
    node.focus();
    node.setSelectionRange(position, position);
  });

  const error = input.errorMessage(fieldState.error);
  const images = imagesOf(input.text);
  const hintText =
    hint && input.status ? (
      <>
        {hint} {GLYPH.middot} {input.status}
      </>
    ) : (
      (hint ?? input.status ?? undefined)
    );
  const tools = [
    { key: 'heading', label: t('richText.heading'), snippet: '## ' },
    { key: 'quote', label: t('richText.quote'), snippet: '> ' },
    { key: 'list', label: t('richText.list'), snippet: '- ' },
    { key: 'link', label: t('richText.link'), snippet: t('richText.linkSnippet') },
  ];
  const isDisabled = disabled || field.disabled;

  function insert(snippet: string) {
    const next = appendBlock(input.text, snippet);
    // Курсор — в конец добавленной разметки: ввод продолжается в ней.
    caret.current = next.length;
    input.change(next);
  }

  function removeImage(line: number) {
    const lines = input.text.split('\n');
    lines.splice(line, 1);
    input.change(lines.join('\n').replace(/\n{3,}/g, '\n\n'));
  }

  const toolClass =
    'flex h-24 cursor-pointer items-center rounded-16 border border-line-strong bg-paper-pure px-9 text-[11px] font-medium whitespace-nowrap text-ink-700 hover:bg-gold-wash-2 disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      counter={maxLength ? { length: input.text.length, max: maxLength } : undefined}
      aside={input.switcher}
      error={error}
      errorId={ids.errorId}
      hint={hintText}
      hintId={ids.hintId}
      wide={wide}
    >
      <div className="overflow-hidden rounded-16 border border-line-field bg-paper-pure focus-within:border-wine-bordeaux focus-within:shadow-[0_0_0_2px_var(--vw-accent-wash)]">
        <div
          role="toolbar"
          aria-label={t('richText.toolbar')}
          className="flex flex-wrap items-center gap-6 border-b border-line-hair bg-surface-sunken px-8 py-6"
        >
          {tools.map((tool) => (
            <button
              key={tool.key}
              type="button"
              disabled={isDisabled}
              onClick={() => insert(tool.snippet)}
              className={toolClass}
            >
              {tool.label}
            </button>
          ))}
          {withImages ? (
            <>
              <span aria-hidden="true" className="h-18 w-1 bg-line-strong" />
              <button
                type="button"
                disabled={isDisabled}
                onClick={() => insert(t('richText.imageSnippet'))}
                className="flex h-24 cursor-pointer items-center rounded-16 border-0 bg-wine-bordeaux px-9 text-[11px] font-semibold whitespace-nowrap text-ink-inverse-pure hover:bg-wine-ink disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('richText.image')}
              </button>
            </>
          ) : null}
          <span className="flex-1" />
          <span className="text-[10.5px] whitespace-nowrap text-ink-400">
            {t('richText.words', { count: wordsOf(input.text) })}
            {withImages ? (
              <>
                {' '}
                {GLYPH.middot} {t('richText.images', { count: images.length })}
              </>
            ) : null}
          </span>
        </div>
        <textarea
          id={ids.id}
          ref={(node) => {
            area.current = node;
            field.ref(node);
          }}
          name={`${field.name}.${input.language}`}
          value={input.text}
          lang={input.language}
          placeholder={placeholder ?? t('richText.placeholder')}
          maxLength={maxLength}
          disabled={isDisabled}
          onChange={(event) => input.change(event.target.value)}
          onBlur={field.onBlur}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(Boolean(error), Boolean(hintText), ids)}
          className="block min-h-168 w-full resize-y border-0 bg-paper-pure px-12 py-10 font-ui text-[13px] leading-[21px] text-ink-900 outline-none placeholder:text-ink-400"
        />
        {withImages ? (
          <div className="flex flex-col border-t border-line-hair">
            {images.length === 0 ? (
              <span className="px-12 py-8 text-[11px] text-ink-400">{t('richText.noImages')}</span>
            ) : (
              images.map((image) => (
                <div
                  key={`${image.line}-${image.url}`}
                  className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-10 border-b border-line-soft px-12 py-7 last:border-b-0"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-28 items-center justify-center rounded-9 bg-paper-deep text-[9px] tracking-[0.08em] text-gold-leaf-3 shadow-[inset_0_0_0_1px_var(--vw-line-hair)]"
                  >
                    IMG
                  </span>
                  <span className="truncate text-[12px] text-ink-900">
                    {image.caption || image.url || t('richText.noCaption')}
                  </span>
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => removeImage(image.line)}
                    className="cursor-pointer border-0 bg-transparent p-0 text-[11px] whitespace-nowrap text-status-error hover:underline"
                  >
                    {t('richText.remove')}
                  </button>
                </div>
              ))
            )}
          </div>
        ) : null}
      </div>
    </FormField>
  );
}
